import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { authApi } from '@/api/endpoints';
import { useAuthStore } from '@/store/auth';
import { toast } from '@/store/toast';
import { Loader } from '@/components/common/Loader';
import { ThemeSwitcher } from '@/components/common/ThemeSwitcher';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';

export default function LoginPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation() as { state?: { from?: string } };
  const setTokens = useAuthStore(s => s.setTokens);
  const setUser = useAuthStore(s => s.setUser);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [loading, setLoading] = useState(false);

  // Sinalizador setado pelo interceptor quando o IP muda no meio da sessao
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem('codeshield_ip_changed') === '1') {
      sessionStorage.removeItem('codeshield_ip_changed');
      toast.error(t('ipChange.loginAlert.midSessionTitle'), t('ipChange.loginAlert.midSessionHint'));
    }
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authApi.login({ email, password, totpCode: needsTotp ? totp : undefined });
      // 200 -> tokens; 202 -> totp_required
      if (res.status === 202) {
        setNeedsTotp(true);
        toast.info(t('auth.errors.totp_required'));
        return;
      }
      const data = res.data as any;
      setTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        accessExpiresAt: data.accessExpiresAtUtc,
        refreshExpiresAt: data.refreshExpiresAtUtc
      });
      setUser({
        userId: data.userId, email: data.email, displayName: data.displayName, role: data.role,
        isActive: true, isTotpEnabled: needsTotp
      });
      toast.success(t('auth.login'), data.displayName);
      nav(loc.state?.from ?? '/', { replace: true });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 202) {
        setNeedsTotp(true);
        toast.info(t('auth.errors.totp_required'));
      } else if (axios.isAxiosError(err) && err.response?.data?.detail === 'ip_authorization_required') {
        // IP novo detectado — backend ja disparou email com link tokenizado
        const masked = err.response.data.maskedEmail || email;
        toast.error(t('ipChange.loginAlert.newIp'), t('ipChange.loginAlert.newIpHint', { email: masked }));
      } else {
        const reason = (err as any)?.response?.data?.detail || 'invalid_credentials';
        const key = `auth.errors.${reason}` as const;
        toast.error(t(key, t('auth.errors.invalid_credentials')));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="flex items-center justify-end gap-2 p-4">
        <LanguageSwitcher /><ThemeSwitcher />
      </header>
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto mb-3 h-14 w-14 rounded-xl bg-primary flex items-center justify-center">
              <ShieldCheck className="h-7 w-7 text-primary-foreground" />
            </div>
            <CardTitle className="text-2xl">{t('app.name')}</CardTitle>
            <CardDescription>{t('auth.loginSubtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input id="email" type="email" required autoComplete="email" autoFocus
                  value={email} onChange={e => setEmail(e.target.value)} disabled={loading || needsTotp} />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground">{t('auth.forgotPassword')}</Link>
                </div>
                <Input id="password" type="password" required autoComplete="current-password"
                  value={password} onChange={e => setPassword(e.target.value)} disabled={loading || needsTotp} />
              </div>
              {needsTotp && (
                <div className="space-y-2 animate-in fade-in slide-up">
                  <Label htmlFor="totp">{t('auth.totpCode')}</Label>
                  <Input id="totp" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={10}
                    autoComplete="one-time-code" required autoFocus
                    value={totp} onChange={e => setTotp(e.target.value)} />
                </div>
              )}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <Loader /> : t('auth.submit')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      <footer className="text-center text-xs text-muted-foreground py-4">
        CodeShield Portal — Anti-Cheat Operations Center
      </footer>
    </div>
  );
}
